import {
  CURRENCY_BY_COUNTRY,
  type CurrencyCode,
  OPERATING_COUNTRIES,
  type OperatingCountry,
  REGIONAL_DEFAULTS,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from './regional';
import type {
  AddressStyle,
  CurrencyStyle,
  DateStyle,
  DateTimeStyle,
  FirstDayOfWeek,
  NumberStyle,
  PhoneStyle,
  ResolvedUnits,
  TimeStyle,
  UnitSystem,
} from './types';

/**
 * Where a preference comes from when the payload does not state it.
 *
 * Two different kinds of source live here, and the difference is worth keeping visible rather than
 * blurring into one "defaults" bag:
 *
 * - **Read from `Intl` at runtime.** Hour cycle, date order and first day of week are all CLDR
 *   facts the platform already knows. Restating them in a table would be a second source of truth
 *   that drifts every time ICU updates.
 * - **Small declared tables.** `Intl` exposes no measurement system, and no standard API maps a
 *   locale to a currency. Those need a table. It is kept to the markets profile-api actually models
 *   (US, CA, MX) rather than pretending to cover the world.
 */

/**
 * Mirrors `UserPreferenceFactory.SETTINGS_BY_LOCALE` — the US (imperial) and MX (metric) bundles.
 *
 * Duplicated on purpose. The backend resolves these itself and normally sends the result, so this
 * only runs when a field is missing. Keeping our own copy means a partial payload still produces a
 * coherent set rather than a mix of imperial and metric.
 */
export const UNITS_BY_SYSTEM: Readonly<Record<UnitSystem, ResolvedUnits>> = {
  US_CUSTOMARY: Object.freeze({
    distance: 'MILES',
    speed: 'MPH',
    capacity: 'GAL_US',
    fuelConsumption: 'MPG_US',
    electricConsumption: 'MI_PER_KWH',
    energy: 'KWH',
    pressure: 'PSI',
    temperature: 'F',
    // Grams per mile rather than pounds per mile, matching the library's own US bundle. A preset
    // that disagreed with it would report UNIT_SYSTEM_MISMATCH against every US payload, since the
    // per-field code the backend sends is compared against this table.
    emission: 'GR_PER_MI',
    mass: 'LB',
  }),
  /**
   * The United Kingdom hybrid, mirroring the library's `imperial()` bundle exactly.
   *
   * Miles and mph, because road signs are; everything else metric, because the UK is metric by law
   * and mixed only by habit. Fuel economy is the imperial gallon, which is roughly 20% larger than
   * the US one, so an unconverted `MPG_US` here would read about 20% low.
   */
  IMPERIAL: Object.freeze({
    distance: 'MILES',
    speed: 'MPH',
    capacity: 'LITER',
    fuelConsumption: 'MPG_UK',
    electricConsumption: 'MI_PER_KWH',
    energy: 'KWH',
    pressure: 'PSI',
    temperature: 'C',
    emission: 'GR_PER_KM',
    mass: 'KG',
  }),
  METRIC: Object.freeze({
    distance: 'KM',
    speed: 'KMH',
    capacity: 'LITER',
    fuelConsumption: 'L_PER_100KM',
    electricConsumption: 'KWH_PER_100KM',
    energy: 'KWH',
    pressure: 'KPA',
    temperature: 'C',
    emission: 'GR_PER_KM',
    mass: 'KG',
  }),
};

/**
 * The countries that are not metric, mirroring the library's own country table.
 *
 * `Intl` has no measurement-system accessor, so this cannot be derived. It stays correct because
 * which countries reject the metric system is not a CLDR detail that shifts with ICU releases.
 * Anything absent is metric.
 */
const SYSTEM_BY_COUNTRY: CountryTable<UnitSystem> = {
  US: 'US_CUSTOMARY',
  LR: 'US_CUSTOMARY',
  MM: 'US_CUSTOMARY',
  GB: 'IMPERIAL',
};

type CountryTable<T> = Readonly<Partial<Record<string, T>>>;

const CURRENCY_STYLE_BY_COUNTRY: CountryTable<CurrencyStyle> = {
  US: 'USD_STYLE',
  CA: 'CAD_STYLE',
  MX: 'MXN_STYLE',
};

const PHONE_STYLE_BY_COUNTRY: CountryTable<PhoneStyle> = {
  US: 'US',
  CA: 'CA',
  MX: 'MX',
};

const ADDRESS_STYLE_BY_COUNTRY: CountryTable<AddressStyle> = {
  MX: 'MX_STANDARD',
};

/** The backend sends a style; `Intl.NumberFormat` needs the ISO code. */
export const CURRENCY_CODE_BY_STYLE: Readonly<
  Record<CurrencyStyle, CurrencyCode>
> = {
  USD_STYLE: 'USD',
  CAD_STYLE: 'CAD',
  MXN_STYLE: 'MXN',
};

/** Both backend presets use two decimals, so there is nothing locale-specific to derive. */
const DEFAULT_NUMBER_STYLE: NumberStyle = 'DECIMAL_2';

/**
 * Canonicalises a language tag, or reports that it cannot be one.
 *
 * `Intl` throws a `RangeError` on a malformed tag and this package must never throw on user data,
 * so every locale funnels through here first. Returning `undefined` rather than the fallback keeps
 * the two cases distinguishable: the caller needs to know whether it substituted a locale, since
 * that is worth reporting, while `en-us` canonicalising to `en-US` is not.
 */
export const normaliseLocale = (
  locale: string | undefined | null
): string | undefined => {
  if (!locale) return undefined;

  try {
    return new Intl.Locale(locale).toString();
  } catch {
    return undefined;
  }
};

/** ISO 3166-1 alpha-2 region, uppercased, or `undefined` for a locale that carries none. */
export const countryOf = (locale: string): string | undefined => {
  try {
    return new Intl.Locale(locale).region?.toUpperCase();
  } catch {
    return undefined;
  }
};

/**
 * The language, script and region of a tag, with any Unicode extension dropped.
 *
 * `fr-CA-u-nu-latn` and `fr-CA` name the same market, so anything keyed by market has to compare
 * base names rather than whole tags.
 */
export const baseNameOf = (locale: string): string | undefined => {
  try {
    return new Intl.Locale(locale).baseName;
  } catch {
    return undefined;
  }
};

/** ISO 639 language subtag, lowercased, or `undefined` for a tag `Intl` cannot read. */
export const languageOf = (locale: string): string | undefined => {
  try {
    return new Intl.Locale(locale).language.toLowerCase();
  } catch {
    return undefined;
  }
};

/** Whether a locale tag is one the product ships and users can select. */
export const isSupportedLocale = (locale?: string): locale is SupportedLocale =>
  Boolean(locale && (SUPPORTED_LOCALES as readonly string[]).includes(locale));

/**
 * Picks the first supported locale from an ordered list of candidates, such as a
 * language fallback chain.
 * @param candidates - Locale tags in order of preference
 * @param fallback - Returned when no candidate is supported
 * @returns The first supported candidate, otherwise the fallback
 */
export const resolveSupportedLocale = (
  candidates: readonly string[],
  fallback: SupportedLocale = REGIONAL_DEFAULTS.locale
): SupportedLocale => candidates.find(isSupportedLocale) ?? fallback;

export const isOperatingCountry = (
  country?: string
): country is OperatingCountry =>
  Boolean(country && Object.hasOwn(CURRENCY_BY_COUNTRY, country));

/**
 * Operating country for a supported ISO currency, or `undefined` when unrecognized.
 *
 * QuickPay-only: invoices expose currency but not region, and Zuora’s payment-method region is a
 * country code. Remove once the backend exposes region (or country of operation) on the invoice path.
 */
export const countryForCurrency = (
  currency?: string
): OperatingCountry | undefined =>
  currency
    ? OPERATING_COUNTRIES.find(
        (country) => CURRENCY_BY_COUNTRY[country] === currency
      )
    : undefined;

/** US customary for the countries that reject metric, the UK hybrid for GB, metric for the rest. */
export const deriveUnitSystem = (locale: string): UnitSystem => {
  const country = countryOf(locale);
  return (country ? SYSTEM_BY_COUNTRY[country] : undefined) ?? 'METRIC';
};

/** The frozen unit bundle implied by the locale's unit system. */
export const deriveUnits = (locale: string): ResolvedUnits =>
  UNITS_BY_SYSTEM[deriveUnitSystem(locale)];

/**
 * 12- or 24-hour clock, from CLDR rather than a country list.
 *
 * `hour: 'numeric'` is required: without an hour field in the request, `resolvedOptions()` omits
 * `hourCycle` entirely.
 */
export const deriveTimeStyle = (locale: string): TimeStyle => {
  try {
    const { hourCycle } = new Intl.DateTimeFormat(locale, {
      hour: 'numeric',
    }).resolvedOptions();

    if (hourCycle === 'h11' || hourCycle === 'h12') return '12H';
    if (hourCycle === 'h23' || hourCycle === 'h24') return '24H';
    // Omitted hourCycle is the same "we don't know" as a throw, so the same fallback.
    return '12H';
  } catch {
    return '12H';
  }
};

/**
 * Day/month/year ordering, read off a formatted date rather than tabulated.
 *
 * Asking `Intl` to format a numeric date and inspecting which part it emits first is the only way
 * to get this without restating CLDR, and it stays right for locales nobody thought to add.
 */
export const deriveDateStyle = (locale: string): DateStyle => {
  try {
    const parts = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    }).formatToParts(new Date(Date.UTC(2026, 0, 2)));

    const order = parts
      .map(({ type }) => type)
      .filter(
        (type): type is 'day' | 'month' | 'year' =>
          type === 'day' || type === 'month' || type === 'year'
      );

    if (order[0] === 'year') return 'YMD';
    if (order[0] === 'day') return 'DMY';
    return 'MDY';
  } catch {
    return 'MDY';
  }
};

/**
 * The combined style, composed from the two fields above.
 *
 * `DATETIME` has no YMD variant even though `DATE` does, so a YMD locale has no faithful code. It
 * borrows the day-first variant, and `date` and `time` stay authoritative for anything that needs
 * to be exact. Raised with the backend; until it is answered this is the lossy edge of the enum.
 */
export const deriveDateTimeStyle = (
  dateStyle: DateStyle,
  timeStyle: TimeStyle
): DateTimeStyle => {
  const dateComponent = dateStyle === 'MDY' ? 'MDY' : 'DMY';
  return `${dateComponent}_${timeStyle}` as DateTimeStyle;
};

/**
 * CLDR Sunday-start markets this package actually models. Used only when `getWeekInfo` /
 * `weekInfo` are missing; with those APIs present, `Intl` is the source of truth.
 */
const SUNDAY_FALLBACK_COUNTRIES: ReadonlySet<string> = new Set([
  'US',
  'CA',
  'MX',
]);

/**
 * First day of the calendar week.
 *
 * `getWeekInfo()` is the CLDR answer and is present in current browsers and Node. It shipped as a
 * `weekInfo` getter first, so both spellings are probed before giving up. ISO numbering: 1 is
 * Monday through 7 Sunday.
 */
export const deriveFirstDayOfWeek = (locale: string): FirstDayOfWeek => {
  try {
    const candidate = new Intl.Locale(locale) as Intl.Locale & {
      getWeekInfo?: () => { firstDay?: number };
      weekInfo?: { firstDay?: number };
    };

    const firstDay =
      candidate.getWeekInfo?.().firstDay ?? candidate.weekInfo?.firstDay;

    if (firstDay === 7) return 'SUNDAY';
    if (firstDay === 6) return 'SATURDAY';
    if (firstDay === 1) return 'MONDAY';
  } catch {
    // Falls through to the regional default below.
  }

  const country = countryOf(locale);
  return country && SUNDAY_FALLBACK_COUNTRIES.has(country)
    ? 'SUNDAY'
    : 'MONDAY';
};

const lookup = <T>(table: CountryTable<T>, locale: string, fallback: T): T => {
  const country = countryOf(locale);
  return (country ? table[country] : undefined) ?? fallback;
};

/** Currency style for the locale's country; `USD_STYLE` when the country has no row. */
export const deriveCurrencyStyle = (locale: string): CurrencyStyle =>
  lookup(CURRENCY_STYLE_BY_COUNTRY, locale, 'USD_STYLE');

/** Phone numbering style for the locale's country; US when the country has no row. */
export const derivePhoneStyle = (locale: string): PhoneStyle =>
  lookup(PHONE_STYLE_BY_COUNTRY, locale, 'US');

/** Address layout for the locale's country; US standard when the country has no row. */
export const deriveAddressStyle = (locale: string): AddressStyle =>
  lookup(ADDRESS_STYLE_BY_COUNTRY, locale, 'US_STANDARD');

/** Both backend presets use two decimals, so there is nothing locale-specific to derive. */
export const deriveNumberStyle = (): NumberStyle => DEFAULT_NUMBER_STYLE;

/**
 * UTC when the profile carries none, and never the host's own zone.
 *
 * Only the backend decides what zone a reader is in. A laptop's zone is a different fact that
 * happens to be shaped the same, and substituting it renders every timestamp on the page shifted
 * while looking entirely plausible — the failure a traveller sees and nobody else can reproduce.
 * UTC is wrong in a way that shows. A zone the backend did send that `Intl` does not recognise is
 * recorded as an issue; a payload naming no zone at all is not, having expressed no preference for
 * the resolver to contradict — so an absent zone reaches UTC quietly, and that is the likelier miss
 * of the two.
 */
export const deriveTimeZone = (): string => 'UTC';
